import React from 'react'
import TeamLogo from '../shared/TeamLogo'
import SocialLinksRow from './SocialLinksRow'

function roundDown(n) {
  if (n < 10) return { value: n, approximate: false }
  if (n < 100) return { value: Math.floor(n / 10) * 10, approximate: true }
  return { value: Math.floor(n / 100) * 100, approximate: true }
}

function buildStatsItems({ team, reviews, translations }) {
  const t = translations?.stats || {}
  const items = []

  // Lessons (show if > 0)
  const lessonsCount = team.completedLessonsCount || 0
  if (lessonsCount > 0) {
    const r = roundDown(lessonsCount)
    const template = r.approximate ? t.lessons : t.lessonsExact
    const fallback = r.approximate ? `${r.value}+ Lessons Completed` : `${r.value} Lessons Completed`
    items.push(template ? template.replace('%{count}', r.value) : fallback)
  }

  // Students (show if >= 10)
  const studentCount = team.studentCount || 0
  if (studentCount >= 10) {
    const r = roundDown(studentCount)
    const fallback = `${r.value}+ Students`
    items.push(t.students ? t.students.replace('%{count}', r.value) : fallback)
  }

  // Reviews (show if >= 5 reviews AND avg >= 3.5)
  // Use pre-computed aggregates from backend (accurate across all reviews, not limited to 50)
  const reviewsCount = team.reviewsCount || 0
  const avgRating = team.averageRating
  if (reviewsCount >= 5 && avgRating && avgRating >= 3.5) {
    const rating = Number(avgRating).toFixed(1)
    const fallback = `⭐ ${rating} (${reviewsCount} Reviews)`
    items.push(
      t.reviews
        ? t.reviews.replace('%{rating}', rating).replace('%{count}', reviewsCount)
        : fallback
    )
  }

  // Need at least 2 items to show the row
  return items.length >= 2 ? items : []
}

export default function TeamHeroSection({ team, colors, onConsultationClick, consultationEnabled, translations, reviews, onFollow, isMember, followCtaLabel, contact, teamSlug }) {

  const statsItems = buildStatsItems({ team, reviews, translations })

  return (
    <section className="pb-6">
      <div className="flex flex-col md:flex-row items-start gap-5">
          {/* Team Logo */}
          <TeamLogo
            team={team}
            size="medium"
            primaryColor={colors.primary}
            className="w-20 h-20 md:w-24 md:h-24 shadow-lg"
          />

          {/* Team Info */}
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold mb-1" style={{ color: '#222222' }}>
              {team.name}
            </h1>
            {/* Team tagline - 一句話標語 */}
            {team.tagline && (
              <p className="text-lg font-medium mb-2" style={{ color: '#222222' }}>
                {team.tagline}
              </p>
            )}
            {/* Team description */}
            {team.description && (
              <p className="text-base leading-relaxed whitespace-pre-line" style={{ color: '#717171' }}>
                {team.description}
              </p>
            )}

            {/* Social proof stats */}
            {statsItems.length > 0 && (
              <p className="text-sm mt-2 font-medium" style={{ color: '#555555' }}>
                {statsItems.join(' · ')}
              </p>
            )}

            {/* Social links — IG-style trust signal row (below stats, above CTA) */}
            {contact && (
              <SocialLinksRow contact={contact} teamSlug={teamSlug} />
            )}

            {/* Hero CTA Buttons */}
            <div className="flex flex-wrap gap-3 mt-4">
              {consultationEnabled && onConsultationClick && (
                <button
                  onClick={() => onConsultationClick({ source: 'hero' })}
                  className="px-6 py-2.5 rounded-xl font-semibold text-white shadow-md hover:shadow-lg transition-all cursor-pointer"
                  style={{ background: colors.primaryGradient }}
                >
                  {translations?.freeConsultation || '聯繫老師'}
                </button>
              )}
            </div>

            {/* Follow CTA removed from hero — relocated to dedicated section below courses */}
          </div>
        </div>
    </section>
  )
}
